import React, {useEffect} from 'react';
import {View, StyleSheet, ScrollView, TouchableOpacity} from 'react-native';
import {
  Portal, Dialog, Text, IconButton, ActivityIndicator, Chip,
} from 'react-native-paper';
import {colors, spacing} from '../../theme';
import {useCouponStore} from '../../store/useCouponStore';

const CouponSelectModal = ({visible, onDismiss, onSelect, orderTotal}) => {
  const {coupons, isLoading, fetchCoupons} = useCouponStore();

  useEffect(() => {
    if (visible) {
      fetchCoupons();
    }
  }, [visible, fetchCoupons]);

  const total = Number(orderTotal || 0);

  return (
    <Portal>
      <Dialog visible={visible} onDismiss={onDismiss} style={styles.dialog}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>Offers</Text>
          <IconButton icon="close" size={20} onPress={onDismiss} />
        </View>
        <Dialog.Content>
          {isLoading ? (
            <ActivityIndicator size="small" color={colors.primary} style={{marginVertical: spacing.xl}} />
          ) : (
            <ScrollView style={styles.scrollArea} showsVerticalScrollIndicator={false}>
              {coupons.length === 0 && (
                <Text style={styles.emptyText}>No coupons available</Text>
              )}
              {coupons.map(coupon => {
                const minNotMet = coupon.minOrderValue && total < Number(coupon.minOrderValue);
                const isExpired = coupon.expiryDate && new Date(coupon.expiryDate) < new Date();
                const disabled = minNotMet || isExpired;

                return (
                  <TouchableOpacity
                    key={coupon.id}
                    style={[styles.couponCard, disabled && styles.couponCardDisabled]}
                    onPress={() => {
                      if (!disabled) {
                        onSelect(coupon);
                        onDismiss();
                      }
                    }}
                    activeOpacity={disabled ? 1 : 0.7}>
                    <View style={styles.couponHeader}>
                      <View style={[styles.badge, disabled && styles.badgeDisabled]}>
                        <Text style={[styles.badgeText, disabled && styles.badgeTextDisabled]}>
                          {coupon.type === 'PERCENTAGE'
                            ? `${coupon.value}%`
                            : `\u20B9${Number(coupon.value).toLocaleString()}`}
                        </Text>
                      </View>
                      <View style={{flex: 1, marginLeft: spacing.sm}}>
                        <Text style={[styles.couponCode, disabled && styles.textDisabled]}>
                          {coupon.code}
                        </Text>
                        <Text style={[styles.couponName, disabled && styles.textDisabled]}>
                          {coupon.name}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.chipRow}>
                      {coupon.minOrderValue > 0 && (
                        <Chip style={[styles.chip, minNotMet && styles.chipError]}>
                          <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>Min {'\u20B9'}{Number(coupon.minOrderValue).toLocaleString()}</Text>
                        </Chip>
                      )}
                      {coupon.maxDiscountValue > 0 && (
                        <Chip style={styles.chip}>
                          <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>Max {'\u20B9'}{Number(coupon.maxDiscountValue).toLocaleString()}</Text>
                        </Chip>
                      )}
                      {coupon.applicability === 'ONCE_PER_CUSTOMER' && (
                        <Chip style={styles.chip}><Text style={{fontSize: 13, color: '#000', marginTop: 3}}>Only Once</Text></Chip>
                      )}
                      {coupon.applicability === 'FIRST_ORDER' && (
                        <Chip style={styles.chip}><Text style={{fontSize: 13, color: '#000', marginTop: 3}}>First Order</Text></Chip>
                      )}
                      {isExpired && (
                        <Chip style={styles.chipError}><Text style={{fontSize: 13, color: '#000', marginTop: 3}}>Expired</Text></Chip>
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}
        </Dialog.Content>
      </Dialog>
    </Portal>
  );
};

const styles = StyleSheet.create({
  dialog: {maxHeight: '80%', borderRadius: 15},
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: spacing.lg,
    paddingRight: spacing.xs,
  },
  title: {fontSize: 18, fontWeight: '700', color: colors.text},
  scrollArea: {maxHeight: 450},
  emptyText: {color: colors.textSecondary, textAlign: 'center', marginVertical: spacing.xl},
  couponCard: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: spacing.sm,
    marginBottom: spacing.sm,
  },
  couponCardDisabled: {
    backgroundColor: colors.background,
    opacity: 0.6,
  },
  couponHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  badge: {
    backgroundColor: colors.primary + '20',
    borderRadius: 6,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    minWidth: 50,
    alignItems: 'center',
  },
  badgeDisabled: {backgroundColor: colors.textSecondary + '20'},
  badgeText: {fontWeight: '700', fontSize: 14, color: colors.primary},
  badgeTextDisabled: {color: colors.textSecondary},
  couponCode: {fontWeight: '700', fontSize: 14, color: colors.text},
  couponName: {fontSize: 12, color: colors.textSecondary, marginTop: 2},
  textDisabled: {color: colors.textSecondary},
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: spacing.xs,
  },
  chip: {
    height: 24,
    paddingVertical: 0,
    backgroundColor: colors.background,
  },
  chipError: {
    height: 24,
    paddingVertical: 0,
    backgroundColor: colors.error + '15',
  },
  chipText: {fontSize: 10, lineHeight: 14},
});

export default CouponSelectModal;
